import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { TrendingIcon } from '@change/ds-icons';

import { Translate, TranslateHtml } from '@change-corgi/core-react/i18n';

import type { SupporterVoicesR2CopyArm } from '#src/app/pages/actions/shared/hooks';

import { AddYourVideoButton } from '../AddYourVideoButton';
import { LoginToAddVideo } from '../LoginToAddVideo';
import { SignToAddVideo } from '../SignToAddVideo';
import { SkipRecording } from '../SkipRecording';
import { StaffAvatar } from '../StaffAvatar';
import { VideoCountProgress } from '../VideoCountProgress';

import { useAddYourVideoInvite } from './hooks/useAddYourVideoInvite';

type CopyProps = {
	r2CopyArm: SupporterVoicesR2CopyArm | undefined;
	username: string | undefined;
	location: string | undefined;
};

/**
 * Keys are literal here rather than resolved in the hook so `no-invalid-translation-keys` can still
 * check them (pattern from `VideoFirstAsk`'s `Headline`).
 */
const InviteHeadline: React.FC<CopyProps> = ({ r2CopyArm, username, location }) => {
	if (r2CopyArm === 'v2_3x_impact') {
		return <Translate value="corgi.petition_actions.r2_ask.v2_3x_impact.headline" />;
	}

	if (r2CopyArm === 'v1_dm_impact') {
		if (username && location) {
			return (
				<Translate value="corgi.petition_actions.r2_ask.v1_dm_impact.headline" replacements={{ username, location }} />
			);
		}
		if (username) {
			return (
				<Translate
					value="corgi.petition_actions.r2_ask.v1_dm_impact.headline_no_location"
					replacements={{ username }}
				/>
			);
		}
		if (location) {
			return (
				<Translate value="corgi.petition_actions.r2_ask.v1_dm_impact.headline_no_name" replacements={{ location }} />
			);
		}
		return <Translate value="corgi.petition_actions.r2_ask.v1_dm_impact.headline_no_name_no_location" />;
	}

	if (username) {
		return <Translate value="corgi.petition_actions.invite.heading.with_name" replacements={{ username }} />;
	}
	return <Translate value="corgi.petition_actions.invite.heading.without_name" />;
};

const InviteExpertMessage: React.FC<Pick<CopyProps, 'r2CopyArm'> & { disableVideoCreation: boolean }> = ({
	r2CopyArm,
	disableVideoCreation,
}) => {
	if (r2CopyArm === 'v2_3x_impact') {
		return <TranslateHtml value="corgi.petition_actions.r2_ask.v2_3x_impact.expert_message" />;
	}

	if (r2CopyArm === 'v1_dm_impact') {
		return <Translate value="corgi.petition_actions.r2_ask.v1_dm_impact.expert_message" />;
	}

	return disableVideoCreation ? (
		<Translate value="corgi.petition_actions.invite.call_to_action_text" />
	) : (
		<Translate value="corgi.petition_actions.invite.call_to_action_video" />
	);
};

export const AddYourVideoInvite: React.FC = () => {
	const {
		data: { capability, disableVideoCreation, r2CopyArm, username, location },
	} = useAddYourVideoInvite();

	return (
		<>
			<div className="text-center" data-testid="add-voice-cta">
				<Text as="h2" variant="heading" size="medium" fontWeight="bold" className="text-typography-strong mb-6">
					<InviteHeadline r2CopyArm={r2CopyArm} username={username} location={location} />
				</Text>
				<StaffAvatar />
				<Text size="medium" className="text-typography-strong italic" expressive>
					<InviteExpertMessage r2CopyArm={r2CopyArm} disableVideoCreation={disableVideoCreation} />
				</Text>
			</div>
			{!disableVideoCreation && <VideoCountProgress />}
			<div className="flex flex-col items-center gap-4">
				{!disableVideoCreation && <AddYourVideoButton />}
				{(capability === 'capable' || capability === 'unsupported-browser') && <SkipRecording />}
				{capability === 'guest-login' && <LoginToAddVideo />}
				{capability === 'has-not-signed' && <SignToAddVideo />}
				{!disableVideoCreation && (
					<div className="flex items-center">
						<TrendingIcon className="mr-2" size={24} gradient />
						<Text variant="body" size="small" className="text-typography-strong">
							<Translate value="corgi.petition_actions.invite.bottom_note" />
						</Text>
					</div>
				)}
			</div>
		</>
	);
};
